import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import * as api from "../../../Auth/auth";
import PageProtectedOnboarding from "../../../components/Page/PageProtectedOnboarding";
import OnbSteps from "../../../components/Steps/OnbSteps";
import ButtonsMainGoTo from "../../../components/Button/ButtonMainGoTo";
import ShippingAddressEditor from "../../../components/Address/ShippingAddressEditor";
import QuestionnaireForm from "./QuestionnaireForm";
import LabVisitConfirmationCard from "../LabvisitStep/LabVisitConfirmationCard";

import * as c from "../../../constants";
import { pool } from "../../../constants";

const VALID_MICROBIOME_STATUSES = [41, 42];
const STATUS_FALLBACK_ROUTES = {
  5: "/LabvisitStep",
  6: "/FirstLoginStep",
};

function NumberedItem({ number, text, accentColor }) {
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
      <Box
        sx={{
          flexShrink: 0,
          width: 32,
          height: 32,
          borderRadius: "50%",
          bgcolor: accentColor,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="number4"
          sx={{ color: "common.white", fontWeight: 700, fontSize: "0.75rem", lineHeight: 1 }}
        >
          {number}
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ lineHeight: 1.4, pt: 0.5 }}>
        {text}
      </Typography>
    </Stack>
  );
}

function cardSx(theme) {
  return {
    border: "1px solid",
    borderRadius: 4,
    overflow: "hidden",
    ...(theme.palette.mode === "light"
      ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
      : {
          bgcolor: "greydark.C900",
          borderColor: "greydark.C400",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
        }),
  };
}

export default function MicrobiomeSampleStep() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state, handleLogout } = useAuth();
  const { t, changeLanguage } = useTranslation();

  const [kit, setKit] = useState(null);
  const [kitLoading, setKitLoading] = useState(true);
  const [kitError, setKitError] = useState(null);

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [labVisitDone, setLabVisitDone] = useState(false);

  useEffect(() => {
    if (state.user?.language) {
      changeLanguage(state.user.language);
    }
  }, [state.user?.language]);

  // onboarding_status in global auth state can be stale (nothing syncs it back into
  // context after a PATCH elsewhere in the onboarding flow) — fetch the current value
  // fresh so this page knows whether to show the 41-view or the 42-view.
  useEffect(() => {
    let mounted = true;

    const fetchStatus = async () => {
      try {
        const res = await axios.get(c.USERDATA);
        if (!mounted) return;
        const fetchedStatus = res.data?.onboarding_status;
        if (!VALID_MICROBIOME_STATUSES.includes(fetchedStatus)) {
          navigate(STATUS_FALLBACK_ROUTES[fetchedStatus] ?? "/Welcome", { replace: true });
          return;
        }
        setStatus(fetchedStatus);
      } finally {
        if (mounted) setStatusLoading(false);
      }
    };

    fetchStatus();
    return () => {
      mounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    let mounted = true;

    const fetchLatestKit = async () => {
      try {
        setKitLoading(true);
        const res = await axios.get(`${c.API_URL}/api/stool-sample-kits/my-latest-kit/`);
        if (mounted) {
          setKit(res.data);
          setKitError(null);
        }
      } catch (err) {
        if (!mounted) return;
        setKitError(
          err.response?.status === 404
            ? t("microstep_kit_none")
            : t("onb_quest_err_kit_load")
        );
      } finally {
        if (mounted) setKitLoading(false);
      }
    };

    fetchLatestKit();
    return () => {
      mounted = false;
    };
  }, []);

  const handleLogoutapi = async () => {
    const response = await api.logout();
    await handleLogout(response);
  };

  // Lab visit section only shows in the 41-view, and only until confirmed in this
  // session. Once confirmed (or if we loaded straight into 42), the questionnaire's
  // own next status/route flips from "advance to 5, go to LabvisitStep" to
  // "advance to 6, go to FirstLoginStep" — see spec's case 1 vs case 2.
  const showLabVisitSection = status === 41 && !labVisitDone;
  const questionnaireNextStatus = showLabVisitSection ? 5 : 6;
  const questionnaireNextRoute = showLabVisitSection ? "/LabvisitStep" : "/FirstLoginStep";

  return (
    <PageProtectedOnboarding
      headerImage={pool}
      brandtext={t("onbsub_brandtext")}
      content={
        <>
          <Grid container sx={{ mb: 5, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Grid size={12} sx={{ width: "100%", maxWidth: 900, px: { xs: 1, sm: 2 }, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <OnbSteps stepNumber={2} />
            </Grid>
          </Grid>

          <Grid container justifyContent="center">
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, mx: "auto", px: { xs: 2, sm: 0 } }}>
              <Typography variant="body2" color="text.secondary">
                {t("microstep_overline")}
              </Typography>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                {t("microstep_title")}
              </Typography>

              <Box sx={{ ...cardSx(theme), p: 3 }}>
                {kitLoading ? (
                  <Grid container justifyContent="center">
                    <CircularProgress size={24} />
                  </Grid>
                ) : kitError ? (
                  <Typography variant="body1" sx={{ mb: 1.5 }}>{kitError}</Typography>
                ) : kit ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body1" sx={{ mb: 0.5 }}>{t("microstep_kit_activated")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("onb_quest_kit_id")}: {kit.kitId} · {t("onb_quest_act_key")}: {kit.activationKey}
                    </Typography>
                  </Box>
                ) : null}

                <ShippingAddressEditor compact emptyKey="microstep_address_empty" />

                <Divider sx={{ my: 2 }} />

                <NumberedItem number={1} text={t("microstep_card_step1")} accentColor={theme.palette.quartiary.C300} />
                <NumberedItem number={2} text={t("microstep_card_step2")} accentColor={theme.palette.quartiary.C300} />

                {statusLoading ? (
                  <Grid container justifyContent="center" sx={{ my: 2 }}>
                    <CircularProgress size={24} />
                  </Grid>
                ) : (
                  <QuestionnaireForm
                    kind="bioma"
                    nextStatus={questionnaireNextStatus}
                    nextRoute={questionnaireNextRoute}
                    buttonOnly
                  />
                )}
              </Box>

              {!statusLoading && showLabVisitSection && (
                <Typography variant="h4" fontWeight={700} sx={{ mt: 6, mb: 3 }}>
                  {t("microstep_labvisit_title")}
                </Typography>
              )}
            </Grid>
          </Grid>

          {!statusLoading && showLabVisitSection && (
            <LabVisitConfirmationCard
              nextStatus={42}
              nextRoute={null} // stay on this page; only flip to the 42 view, see onConfirmed
              onConfirmed={() => setLabVisitDone(true)}
              contained
            />
          )}

          <Grid container sx={{ mt: 4, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Grid size={{ xs: 12 }} sx={{ maxWidth: 600, display: "flex", justifyContent: "center", alignItems: "center" }}>
              <ButtonsMainGoTo name={t("onb_snc_logout")} to="/Login" onClick={handleLogoutapi} />
            </Grid>
          </Grid>
        </>
      }
    />
  );
}
